import { useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { hmacSha256Hex } from '@/lib/hmac'
import { BlockTypeSelect } from './components/BlockTypeSelect'
import { TargetField } from './components/TargetField'
import { ReasonTemplates, type TemplateOption } from './components/ReasonTemplates'
import { ConfirmRequestDialog } from './components/ConfirmRequestDialog'
import type { BVNInputHandle } from './components/BVNInput'
import { useRequestBlock } from './hooks/useBlockMutations'
import { usePepper } from './hooks/usePepper'
import type { BlockSource, BlockType, RequestBlockPayload } from './types'

const REASON_MIN = 20
const SOURCES: ReadonlyArray<{ value: BlockSource; label: string }> = [
  { value: 'manual', label: 'Manual' },
  { value: 'alert', label: 'Alert' },
  { value: 'case', label: 'Case' },
  { value: 'regulatory_order', label: 'Regulatory order' },
]

/**
 * AF-128 — Block request page.
 *
 * Single-page form. Submission is two-step: validate → ConfirmRequestDialog
 * preview → real POST. BVN inputs hash client-side via HMAC-SHA256(pepper,
 * raw_bvn) so the raw BVN never crosses the wire.
 */
export function BlockRequestPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: pepper, isLoading: pepperLoading } = usePepper()
  const requestBlock = useRequestBlock()

  // AF-132 — When called from Account Detail's Apply PND we land here
  // with prefill query string (`?type=account&bank_code=...&nuban=...&reason=PND%3A...&source=manual&source_reference=...`).
  // Read once on mount; subsequent edits are driven by the user.
  const prefill = useMemo(() => {
    const t = searchParams.get('type')
    const allowedTypes: BlockType[] = [
      'account',
      'customer',
      'card',
      'bvn',
      'device',
      'ip',
      'terminal',
    ]
    return {
      type: (allowedTypes as readonly string[]).includes(t ?? '')
        ? (t as BlockType)
        : null,
      bank_code: searchParams.get('bank_code'),
      nuban: searchParams.get('nuban'),
      identifier: searchParams.get('identifier'),
      reason: searchParams.get('reason'),
      source: searchParams.get('source') as BlockSource | null,
      source_reference: searchParams.get('source_reference'),
    }
    // We intentionally only read URL params once at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Form state.
  const [type, setType] = useState<BlockType>(prefill.type ?? 'account')
  const [identifier, setIdentifier] = useState(prefill.identifier ?? '')
  const [account, setAccount] = useState({
    bankCode: prefill.type === 'account' ? prefill.bank_code ?? null : null,
    nuban: prefill.type === 'account' ? prefill.nuban ?? '' : '',
  })
  const [bvnValid, setBvnValid] = useState(false)
  const bvnRef = useRef<BVNInputHandle>(null)
  const [reason, setReason] = useState(prefill.reason ?? '')
  const [source, setSource] = useState<BlockSource>(prefill.source ?? 'manual')
  const [sourceReference, setSourceReference] = useState(prefill.source_reference ?? '')
  const [effectiveFrom, setEffectiveFrom] = useState(() => toLocalIso(new Date()))
  const [effectiveTo, setEffectiveTo] = useState('')
  const [isRegulatory, setIsRegulatory] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Reset target-related state when the block type flips so old values
  // don't accidentally submit. Done at the source of the change (the
  // BlockTypeSelect onChange) — keeps it out of useEffect so the lint
  // `set-state-in-effect` rule is satisfied.
  const handleTypeChange = (next: BlockType): void => {
    if (next === type) return
    setType(next)
    setIdentifier('')
    setAccount({ bankCode: null, nuban: '' })
    setBvnValid(false)
    bvnRef.current?.reset()
  }

  const targetValid = useMemo(() => {
    if (type === 'account') return account.bankCode !== null && account.nuban.length === 10
    if (type === 'bvn') return bvnValid
    if (type === 'ip') {
      return /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/.test(
        identifier,
      )
    }
    return identifier.trim().length > 0
  }, [type, account, identifier, bvnValid])

  const reasonValid = reason.trim().length >= REASON_MIN
  const formValid = targetValid && reasonValid

  const targetLabel = useMemo(() => buildTargetLabel(type, account, identifier), [
    type,
    account,
    identifier,
  ])

  const handleTemplate = (t: TemplateOption): void => {
    setReason(t.body)
    if (t.regulatory) {
      setIsRegulatory(true)
      setSource('regulatory_order')
    }
  }

  const handlePreview = () => {
    setSubmitError(null)
    if (!formValid) return
    setConfirmOpen(true)
  }

  const handleConfirmSubmit = async () => {
    setSubmitError(null)
    try {
      const payload = await buildPayload({
        type,
        account,
        identifier,
        reason,
        source,
        sourceReference,
        effectiveFrom,
        effectiveTo,
        isRegulatory,
        bvnRef,
        pepper: pepper ?? null,
      })
      const result = await requestBlock.mutateAsync(payload)
      setConfirmOpen(false)
      void navigate(`/blocks/${result.block.id}`)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Submission failed')
    }
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">Request block</h1>
        <p className="text-sm text-muted-fg">
          Two-step maker-checker flow. Once you submit, an approver other than
          you must sign off before the block becomes active.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>What to block</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="block-type">Type</Label>
            <BlockTypeSelect id="block-type" value={type} onChange={handleTypeChange} />
          </div>

          <TargetField
            type={type}
            identifier={identifier}
            setIdentifier={setIdentifier}
            account={account}
            setAccount={setAccount}
            bvnRef={bvnRef}
            bvnPepperReady={!pepperLoading && pepper !== undefined}
            bvnValid={bvnValid}
            setBvnValid={setBvnValid}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reason</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <ReasonTemplates onPick={handleTemplate} />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="block-reason">
              Reason <span className="text-muted-fg">(min {REASON_MIN} chars)</span>
            </Label>
            <textarea
              id="block-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe the trigger and the evidence in plain language."
              className="min-h-[120px] rounded border border-input bg-bg p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-invalid={reason.length > 0 && !reasonValid ? true : undefined}
            />
            <div className="flex items-center justify-between text-xs">
              <span className={reasonValid ? 'text-muted-fg' : 'text-status-pending'}>
                {reason.length} / {REASON_MIN} min
              </span>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isRegulatory}
                  onChange={(e) => {
                    setIsRegulatory(e.target.checked)
                    if (e.target.checked) setSource('regulatory_order')
                  }}
                  className="h-3.5 w-3.5 accent-priority-critical"
                />
                <span>This block is regulatory</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Source &amp; duration</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="block-source">Source</Label>
              <select
                id="block-source"
                value={source}
                onChange={(e) => setSource(e.target.value as BlockSource)}
                className="h-9 rounded border border-input bg-bg px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {SOURCES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="block-source-ref">Source reference</Label>
              <Input
                id="block-source-ref"
                value={sourceReference}
                onChange={(e) => setSourceReference(e.target.value)}
                placeholder="A-2026-…, C-1234, NFIU-…"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="block-from">Effective from</Label>
              <Input
                id="block-from"
                type="datetime-local"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="block-to">Effective to</Label>
              <Input
                id="block-to"
                type="datetime-local"
                value={effectiveTo}
                onChange={(e) => setEffectiveTo(e.target.value)}
                placeholder="Indefinite if blank"
              />
              <p className="text-xs text-muted-fg">Leave blank for an indefinite block.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        {submitError ? (
          <p className="text-sm text-destructive">{submitError}</p>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => void navigate('/blocks')}>
            Cancel
          </Button>
          <Button onClick={handlePreview} disabled={!formValid}>
            Preview &amp; submit
          </Button>
        </div>
      </div>

      <ConfirmRequestDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={() => void handleConfirmSubmit()}
        pending={requestBlock.isPending}
        preview={{
          type,
          targetLabel,
          reason,
          source,
          sourceReference: sourceReference || null,
          effectiveFrom: new Date(effectiveFrom).toISOString(),
          effectiveTo: effectiveTo ? new Date(effectiveTo).toISOString() : null,
          isRegulatory,
        }}
      />
    </div>
  )
}

function toLocalIso(d: Date): string {
  // datetime-local needs YYYY-MM-DDTHH:mm with no timezone suffix.
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`
}

function buildTargetLabel(
  type: BlockType,
  account: { bankCode: string | null; nuban: string },
  identifier: string,
): string {
  if (type === 'account') {
    const masked =
      account.nuban.length === 10
        ? `${account.nuban.slice(0, 3)}****${account.nuban.slice(7)}`
        : account.nuban
    return `Bank ${account.bankCode ?? '—'} · ${masked}`
  }
  if (type === 'bvn') return 'BVN ***hashed***'
  return `${capitalise(type)} ${identifier}`
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

interface BuildPayloadArgs {
  type: BlockType
  account: { bankCode: string | null; nuban: string }
  identifier: string
  reason: string
  source: BlockSource
  sourceReference: string
  effectiveFrom: string
  effectiveTo: string
  isRegulatory: boolean
  bvnRef: React.RefObject<BVNInputHandle | null>
  pepper: string | null
}

async function buildPayload(args: BuildPayloadArgs): Promise<RequestBlockPayload> {
  const base: RequestBlockPayload = {
    type: args.type,
    target_identifier: '',
    reason: args.reason.trim(),
    source: args.source,
    source_reference: args.sourceReference || undefined,
    effective_from: new Date(args.effectiveFrom).toISOString(),
    effective_to: args.effectiveTo ? new Date(args.effectiveTo).toISOString() : undefined,
    is_regulatory: args.isRegulatory || args.source === 'regulatory_order',
  }
  if (args.type === 'account') {
    return {
      ...base,
      target_identifier: `${args.account.bankCode}:${args.account.nuban}`,
    }
  }
  if (args.type === 'bvn') {
    const raw = args.bvnRef.current?.readRaw()
    if (!raw) throw new Error('BVN must be 11 digits.')
    if (!args.pepper) throw new Error('Session pepper unavailable; reload and retry.')
    const bvn_hash = await hmacSha256Hex(args.pepper, raw)
    return { ...base, target_identifier: '', bvn_hash }
  }
  return { ...base, target_identifier: args.identifier.trim() }
}
